import { useCallback, useRef, useState } from 'react';
import axios from 'axios';
import { useToast } from '../context/ToastContext';
import {
  AddMachineRepairMessagePayload,
  CreateMachineRepairThreadPayload,
  MachineRepairThread,
  normalizeThreadListResponse,
  normalizeThreadResponse,
} from '../types/machineRepairRequests';

interface ThreadFilter {
  rnWorkOrder: string;
  subprocessId: string;
  machineId: string;
}

const apiUrl = process.env.REACT_APP_API_URL ?? '';
const baseUrl = `${apiUrl}/api/machine-repair-requests`;

const bumpMainNumber = (number: string): string => {
  const match = number.match(/^(.*)\/MAIN\/(\d+)$/i);
  if (!match) return '';
  return `${match[1]}/MAIN/${String(Number(match[2]) + 1).padStart(2, '0')}`;
};

export const useMachineRepairRequests = () => {
  const { showToast } = useToast();
  const [threads, setThreads] = useState<MachineRepairThread[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [nextNumber, setNextNumber] = useState('');
  const filterRef = useRef<ThreadFilter | null>(null);

  const authHeaders = () => {
    const token = localStorage.getItem('token');
    return { 'x-auth-token': token || '', 'Content-Type': 'application/json' };
  };

  const loadThreads = useCallback(async (filter: ThreadFilter, targetPage = 1) => {
    filterRef.current = filter;
    setLoading(true);
    try {
      const response = await axios.get(baseUrl, {
        headers: authHeaders(),
        params: {
          rnWorkOrder: filter.rnWorkOrder,
          subprocessId: filter.subprocessId,
          machineId: filter.machineId,
          page: targetPage,
        },
      });
      const envelope = normalizeThreadListResponse(response.data);
      setThreads((prev) => (targetPage === 1 ? envelope.data : [...prev, ...envelope.data]));
      setPage(envelope.page);
      setTotalPages(envelope.totalPages);
      if (targetPage === 1) {
        if (envelope.nextNumber) {
          setNextNumber(envelope.nextNumber);
        } else {
          // Fallback: bump from highest assigned number currently loaded.
          const maxAssigned = envelope.data
            .map((thread) => thread.number || '')
            .filter(Boolean)
            .sort((a, b) => {
              const aSeq = Number((a.match(/\/MAIN\/(\d+)$/i) || [])[1] || 0);
              const bSeq = Number((b.match(/\/MAIN\/(\d+)$/i) || [])[1] || 0);
              return bSeq - aSeq;
            })[0];
          if (maxAssigned) setNextNumber(bumpMainNumber(maxAssigned));
        }
      }
    } catch (err: any) {
      const message = err.response?.data?.message || err.message || 'Failed to load repair requests';
      showToast(message, 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  const loadMore = useCallback(async () => {
    if (!filterRef.current || page >= totalPages) return;
    await loadThreads(filterRef.current, page + 1);
  }, [loadThreads, page, totalPages]);

  const createThread = useCallback(async (payload: CreateMachineRepairThreadPayload): Promise<MachineRepairThread | null> => {
    try {
      const response = await axios.post<unknown>(baseUrl, payload, { headers: authHeaders() });
      const thread = normalizeThreadResponse(response.data);
      if (!thread) {
        showToast('Failed to send repair request', 'error');
        return null;
      }
      setThreads((prev) => [thread, ...prev]);
      const envelope = response.data as { nextNumber?: string };
      if (typeof envelope.nextNumber === 'string' && envelope.nextNumber) {
        setNextNumber(envelope.nextNumber);
      } else if (thread.number) {
        setNextNumber(bumpMainNumber(thread.number));
      }
      return thread;
    } catch (err: any) {
      const message = err.response?.data?.message || err.message || 'Failed to send repair request';
      showToast(message, 'error');
      return null;
    }
  }, [showToast]);

  const addMessage = useCallback(async (threadId: string, text: string, dateTo?: string | null): Promise<MachineRepairThread | null> => {
    try {
      const payload: AddMachineRepairMessagePayload = { text };
      if (dateTo !== undefined) payload.dateTo = dateTo;
      const response = await axios.post<unknown>(`${baseUrl}/${threadId}/messages`, payload, { headers: authHeaders() });
      const thread = normalizeThreadResponse(response.data);
      if (!thread) {
        showToast('Failed to send reply', 'error');
        return null;
      }
      setThreads((prev) => prev.map((existing) => (existing._id === thread._id ? thread : existing)));
      return thread;
    } catch (err: any) {
      const message = err.response?.data?.message || err.message || 'Failed to send reply';
      showToast(message, 'error');
      return null;
    }
  }, [showToast]);

  const closeThread = useCallback(async (threadId: string): Promise<MachineRepairThread | null> => {
    try {
      const response = await axios.patch<unknown>(`${baseUrl}/${threadId}/close`, {}, { headers: authHeaders() });
      const thread = normalizeThreadResponse(response.data);
      if (!thread) {
        showToast('Failed to close request', 'error');
        return null;
      }
      setThreads((prev) => prev.map((existing) => (existing._id === thread._id ? thread : existing)));
      return thread;
    } catch (err: any) {
      const message = err.response?.data?.message || err.message || 'Failed to close request';
      showToast(message, 'error');
      return null;
    }
  }, [showToast]);

  const refreshNextNumber = useCallback(async (rnWorkOrder: string) => {
    try {
      const response = await axios.get(`${baseUrl}/next-number`, {
        headers: authHeaders(),
        params: { rnWorkOrder },
      });
      const payload = response.data as { data?: { number?: string }; number?: string };
      const value = payload?.data?.number || payload?.number || '';
      if (value) setNextNumber(value);
      return value;
    } catch {
      return '';
    }
  }, []);

  const hydrateThreads = useCallback((next: MachineRepairThread[], nextNum?: string) => {
    setThreads(next);
    setPage(1);
    setTotalPages(1);
    if (nextNum) setNextNumber(nextNum);
  }, []);

  return {
    threads,
    loading,
    page,
    totalPages,
    nextNumber,
    setNextNumber,
    loadThreads,
    loadMore,
    createThread,
    addMessage,
    closeThread,
    refreshNextNumber,
    hydrateThreads,
  };
};
